"use client"
import { useState, useEffect } from "react"
import { Clock, User, FileText, Loader2, X, Edit, Save, Plus, Trash2 } from "lucide-react"
import { postAws } from "../postAws"
import { renderLatex } from "./utils/latex-renderer"
import QuestionEditorModal from "./AddQuestionEditorModal"
import { generatePresignedUrl } from "../postFileAws"
const API_URL = "https://api-dev.aurait.ai/QuestionBank/invocation"

const AddQuestionQuestionbankEditExtractedQuestions = ({ bookId, showModal, onClose, initialImageUris = [] }) => {
  const [selectedBook, setSelectedBook] = useState(null)
  const [bookLoading, setBookLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [savingAllQuestions, setSavingAllQuestions] = useState(false)
  const [deletingQuestionId, setDeletingQuestionId] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [questionToDelete, setQuestionToDelete] = useState(null)
  const [imageUris, setImageUris] = useState(initialImageUris)
  const [modalImageUris, setModalImageUris] = useState(initialImageUris)

  const [presignedMap, setPresignedMap] = useState({})

  // Infer a practical subtype for rendering
  const inferQuestionSubtype = (q = {}) => {
    const t = String(q.question_type || q.original_question_type || "").toLowerCase()
    if (t.includes("assert")) return "assertion"
    if (t.includes("matrix") || t.includes("match")) return "matrix"
    if (t.includes("integer") || t.includes("numeric")) return "numeric"
    if (t.includes("comprehension") || (q.passage && Array.isArray(q.questions))) return "comprehension"
    if (q.options && Object.keys(q.options || {}).length > 0) {
      if (t.includes("multi")) return "mcq-multi"
      return "mcq"
    }
    return "short"
  }

  // Normalize free-form answers (short/one-word/numeric/true-false when not given as options)
  const normalizeFreeformAnswers = (node) => {
    const raw = node?.correct_answer ?? node?.correctAnswer ?? node?.answer ?? null

    // Allow array of acceptable answers
    if (Array.isArray(raw)) {
      // stringify but keep set semantics
      const list = raw.map((v) => String(v)).filter(Boolean)
      return { kind: "list", list }
    }
    if (raw != null) {
      return { kind: "single", value: String(raw) }
    }
    return { kind: "none" }
  }

  // Matrix/Match normalizer: supports array of pairs, or object map
  // e.g. [["A","P"],["B","Q"]] OR { "A":"P", "B":"Q" }
  const normalizeMatrixPairs = (node) => {
    const raw = node?.correct_answer ?? node?.correctAnswer ?? node?.answer ?? null
    if (Array.isArray(raw)) {
      return raw
        .map((pair) =>
          Array.isArray(pair)
            ? [String(pair[0]), String(pair[1])]
            : pair && typeof pair === "object"
              ? [String(pair.left ?? pair.key ?? ""), String(pair.right ?? pair.value ?? "")]
              : null,
        )
        .filter(Boolean)
    }
    if (raw && typeof raw === "object") {
      return Object.entries(raw).map(([k, v]) => [String(k), String(v)])
    }
    return []
  }

  // --- Normalize answers from any server shape into a consistent structure ---
  // Returns one of:
  //   { type: "options", answers: ["a","c"], isMultiple: true|false }
  //   { type: "numeric", value: "42" }
  const normalizeOptionAnswers = (node) => {
    // Prefer the canonical field, then fall back to legacy aliases
    const raw =
      node?.correct_answer !== undefined
        ? node.correct_answer
        : node?.correctAnswer !== undefined
          ? node.correctAnswer
          : node?.answer !== undefined
            ? node.answer
            : null

    // Handle structured backend formats
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const kind = String(raw.kind || "").toLowerCase()

      // Single correct answer: {"kind": "single", "option": "c"}
      if (kind === "single" && raw.option != null) {
        return { type: "options", answers: [String(raw.option).toLowerCase()], isMultiple: false }
      }

      // Multiple correct answers: {"kind": "multi", "options": ["a", "b", "c"]}
      if ((kind === "multiple" || kind === "multi") && Array.isArray(raw.options)) {
        const answers = raw.options.map((x) => String(x).toLowerCase())
        return { type: "options", answers, isMultiple: answers.length > 1 }
      }

      // Numeric answers: {"kind": "numeric", "value": 8} or {"kind": "numeric", "value": [8, 9]}
      if (kind === "numeric" && raw.value !== undefined) {
        if (Array.isArray(raw.value)) {
          const values = raw.value.map((x) => String(x))
          return { type: "numeric", values, isMultiple: values.length > 1 }
        } else {
          return { type: "numeric", values: [String(raw.value)], isMultiple: false }
        }
      }

      // Legacy object formats
      if (raw.options && Array.isArray(raw.options)) {
        const answers = raw.options.map((x) => String(x).toLowerCase())
        return { type: "options", answers, isMultiple: answers.length > 1 }
      }
      if (raw.option != null) {
        return { type: "options", answers: [String(raw.option).toLowerCase()], isMultiple: false }
      }
      if (raw.value != null) {
        if (Array.isArray(raw.value)) {
          const answers = raw.value.map((x) => String(x).toLowerCase())
          return { type: "options", answers, isMultiple: answers.length > 1 }
        } else {
          return { type: "options", answers: [String(raw.value).toLowerCase()], isMultiple: false }
        }
      }
    }

    // Array format: ["a"] or ["a", "b"] or [8, 9]
    if (Array.isArray(raw)) {
      const answers = raw.map((x) => String(x).toLowerCase())
      return { type: "options", answers, isMultiple: answers.length > 1 }
    }

    // Plain scalar (string/number)
    if (raw != null) {
      return { type: "options", answers: [String(raw).toLowerCase()], isMultiple: false }
    }

    return { type: "options", answers: [], isMultiple: false }
  }

  // Case-insensitive map for options lookups (A vs a)
  const toLowerKeyed = (obj = {}) =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [String(k).toLowerCase(), v]))

  // --- helpers ---
  const cleanS3 = (s = "") =>
    String(s)
      .trim()
      .replace(/^[('"]+|[)'"]+$/g, "") // strip quotes/closing parens if any

  // capture group 1 returns the uri, avoids the dangling ")" bug
  const mdImgRe = /!\[[^\]]*\]$$(s3:\/\/[^)]+)$$/gi
  const bareS3Re = /s3:\/\/[^\s"'\\)]+/gi
  // extract all S3 URIs from a string (markdown or plain)
  const extractS3FromString = (txt = "") => {
    const out = new Set()
    let m
    while ((m = mdImgRe.exec(txt))) out.add(cleanS3(m[1]))
    for (const b of txt.match(bareS3Re) || []) out.add(cleanS3(b))
    return [...out]
  }

  // extract from an object (question/book)
  const extractS3FromObject = (obj) => {
    const seen = new Set()
    const walk = (v) => {
      if (!v) return
      if (typeof v === "string") extractS3FromString(v).forEach((u) => seen.add(u))
      else if (Array.isArray(v)) v.forEach(walk)
      else if (typeof v === "object") Object.values(v).forEach(walk)
    }
    walk(obj)
    return [...seen]
  }

  const ensurePresigned = async (uris = []) => {
    const todo = uris.filter((u) => u && !presignedMap[u])
    if (todo.length === 0) return
    const map = {}
    for (const u of todo) {
      try {
        map[u] = await generatePresignedUrl(u)
      } catch {
        map[u] = null
      }
    }
    setPresignedMap((prev) => ({ ...prev, ...map }))
  }

  // Turn markdown images into <img src="..."> and swap S3 -> presigned for display
  const toHtmlWithImages = (raw = "") => {
    // 1) render LaTeX first (existing util)
    const html = renderLatex(raw || "")
    // 2) convert markdown images to <img src="s3://...">
    const imgified = html.replace(
      mdImgRe,
      (_m, s3) =>
        `<img alt="Image" src="${cleanS3(s3)}" style="max-width:100%;height:auto;display:inline-block;vertical-align:middle;margin:5px 0;border-radius:6px;" />`,
    )
    // 3) swap any <img src="s3://..."> to presigned for preview only
    return imgified.replace(/src="(s3:\/\/[^"]+)"/gi, (_m, s3) => {
      const uri = cleanS3(s3)
      return `src="${presignedMap[uri] || uri}"`
    })
  }

  // drop-in replacement
  const presignBatch = async (uris = []) => {
    // choose whichever presign function exists
    let presignFn = null
    try {
      if (typeof generatePresignedUrl === "function") presignFn = generatePresignedUrl
    } catch {}
    try {
      if (!presignFn && typeof generatePresignedUrl === "function") presignFn = generatePresignedUrl
    } catch {}
    if (!presignFn) {
      console.error("No presign function found (expected generatedpresignedurl or generatePresignedUrl).")
      return {}
    }

    // only s3 + jpg/png
    const isImage = (u) => /^s3:\/\//i.test(u) && /\.(?:png|jpe?g)(?:\?|$)/i.test((u || "").split("?")[0])

    // dedupe + skip already presigned
    const todo = Array.from(new Set((uris || []).filter(isImage))).filter((u) => !presignedMap?.[u])
    if (!todo.length) return {}

    // presign in parallel (safe)
    const results = await Promise.allSettled(
      todo.map(async (u) => {
        const url = await presignFn(u) // <- use your imported helper
        return [u, url]
      }),
    )

    // build { s3Uri: presignedUrl }
    const out = {}
    for (const r of results) {
      if (r.status === "fulfilled") {
        const [u, url] = r.value || []
        if (u && url) out[u] = url
      }
    }
    return out
  }

  useEffect(() => {
    ;(async () => {
      const map = await presignBatch(imageUris)
      if (map && Object.keys(map).length) {
        setPresignedMap((prev) => ({ ...prev, ...map }))
      }
    })()
  }, [imageUris])
  // Heuristic image-URI extractor
  // Heuristic image-URI extractor (PDFs filtered out)
  const extractImageUris = (obj) => {
    const seen = new Set()
    const allowedExt = /\.(png|jpe?g|gif|webp|svg|bmp|tiff?)$/i
    const looksLikeImagePath = /\/(images?|img|media)\//i

    const isImg = (s) =>
      typeof s === "string" &&
      (/^data:image\/(?:png|jpeg)/i.test(s) || // data: only png/jpeg
        (/^s3:\/\//i.test(s) && allowedExt.test(s)) || // s3: must end in .png/.jpg/.jpeg
        (/^https?:\/\//i.test(s) && allowedExt.test(s)))
    const pushS3FromMarkdown = (s) => {
      if (typeof s !== "string") return
      // ![alt](s3://bucket/key.png)
      const md = s.match(/!\[[^\]]*\]$$(s3:\/\/[^)]+)$$/gi) || []
      md.forEach((m) => {
        const uri = m.replace(/^!\[[^\]]*\]$$|$$$/g, "")
        if (allowedExt.test(uri) || looksLikeImagePath.test(uri)) seen.add(uri)
      })
      // bare s3://... inside text
      const bare = s.match(/s3:\/\/[^\s<>"')]+/gi) || []
      bare.forEach((uri) => {
        if (allowedExt.test(uri) || looksLikeImagePath.test(uri)) seen.add(uri)
      })
    }
    const walk = (v) => {
      if (!v) return
      if (typeof v === "string") {
        if (isImg(v)) seen.add(v)
        pushS3FromMarkdown(v)
        return
      }
      if (Array.isArray(v)) return v.forEach(walk)
      if (typeof v === "object") for (const val of Object.values(v)) walk(val)
    }

    walk(obj)
    return Array.from(seen)
  }

  const fetchBookById = async (id) => {
    try {
      setBookLoading(true)
      setError(null)
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classroomId")

      const requestBody = {
        request: "get-book-by-id",
        body: {
          id: id,
          teacherId: teacherId || "",
          classroomId: classroomId || "",
        },
      }

      const response = await postAws(API_URL, "POST", requestBody)

      if (response && response.statusCode === 200) {
        const book = response.body.book

        const sortedQuestions = book.questions.sort((a, b) => (a.question_number || 0) - (b.question_number || 0))

        // collect images: prefer book.images, also scan the whole book (filters .pdf)
        // collect S3 URIs from server + scan all text for ![...](s3://...) and bare s3://
        const serverImgs = Array.isArray(book.images) ? book.images.map(cleanS3) : []
        const scannedS3 = extractS3FromObject(book)
        const merged = Array.from(new Set([...(initialImageUris || []), ...serverImgs, ...scannedS3])).map(cleanS3)
        console.log("[images][EditExtracted] merged image URIs:", merged)

        setImageUris(merged)
        // presign S3 URIs found anywhere in the book/questions
        try {
          const s3s = extractS3FromObject(book)
          const map = await presignBatch(s3s)
          if (map && Object.keys(map).length) setPresignedMap((p) => ({ ...p, ...map }))
        } catch {}

        setSelectedBook({ ...book, questions: sortedQuestions })
      } else {
        setError("Failed to fetch book details")
      }
    } catch (err) {
      setError("Error fetching book details: " + err.message)
      console.error("Error fetching book details:", err)
    } finally {
      setBookLoading(false)
    }
  }

  useEffect(() => {
    if (showModal && bookId) {
      fetchBookById(bookId)
    } else {
      setSelectedBook(null)
    }
  }, [showModal, bookId])

  useEffect(() => {
    if (!selectedBook?.questions) return
    const uris = extractS3FromObject(selectedBook.questions) // reuses your helper
    ;(async () => {
      const map = await presignBatch(uris)
      if (map && Object.keys(map).length) {
        setPresignedMap((prev) => ({ ...prev, ...map }))
      }
    })()
  }, [selectedBook?.questions])
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800"
      case "processing":
        return "bg-yellow-100 text-yellow-800"
      case "failed":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const handleEditQuestionClick = (question) => {
    const base = imageUris || []
    const fromQuestion = extractImageUris(question)
    const isAllowed = (u) => /\.(?:png|jpe?g)(?:\?|$)/i.test((u || "").split("?")[0])
    const forModal = Array.from(new Set([...base, ...fromQuestion])).filter(isAllowed)

    console.log("[images][Edit→EditorModal] Passing", forModal.length, "image URIs:", forModal)

    setModalImageUris(forModal)

    if (question.question_type === "comprehension" || question.question_type === "paragraph") {
      const paragraphGroup = {
        ...question,
        isParagraphGroup: true,
        passage: question.passage,
        questions: question.questions || [question],
      }
      setEditingQuestion(paragraphGroup)
    } else {
      setEditingQuestion(question)
    }
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingQuestion(null)
  }
  const getS3UrisFromString = (s) => {
    if (typeof s !== "string") return []
    const set = new Set()
    ;(s.match(/!\[[^\]]*\]$$(s3:\/\/[^)]+)$$/gi) || []).forEach((m) => {
      set.add(m.replace(/^!\[[^\]]*\]$$|$$$/g, ""))
    })
    ;(s.match(/s3:\/\/[^\s<>"')]+/gi) || []).forEach((u) => set.add(u))
    return Array.from(set)
  }

  const extractImagesFromQuestion = (q) => {
    const uris = new Set()
    const add = (arr) => arr.forEach((u) => /^s3:\/\//i.test(u) && uris.add(u))
    if (!q) return []
    add(getS3UrisFromString(q.question_text))
    add(getS3UrisFromString(q.passage))
    add(getS3UrisFromString(q.assertion))
    add(getS3UrisFromString(q.reason))
    if (q.options) Object.values(q.options).forEach((v) => add(getS3UrisFromString(v)))
    if (q.column_1) Object.values(q.column_1).forEach((v) => add(getS3UrisFromString(v)))
    if (q.column_2) Object.values(q.column_2).forEach((v) => add(getS3UrisFromString(v)))
    if (Array.isArray(q.questions))
      q.questions.forEach((sq) => {
        add(getS3UrisFromString(sq?.question_text))
        if (sq?.options) Object.values(sq.options).forEach((v) => add(getS3UrisFromString(v)))
      })
    if (Array.isArray(q.source_references)) q.source_references.forEach((r) => add(getS3UrisFromString(r?.text)))
    return Array.from(uris)
  }

  // Renders a compact "Correct Answer(s)" chip list.
  // Accepts: string | number | Array<string|number>
  // Also tolerates objects like { option: "c" } | { options:["a","d"] } | { value: 8 } | { selected:"ab" }
  function renderCorrectAnswerBlock(answer) {
    if (answer === undefined || answer === null) return null

    const notNil = (v) => v !== undefined && v !== null
    let vals = []
    let isMultiple = false

    // Handle different answer formats from backend
    if (Array.isArray(answer)) {
      // Direct array format: ["a", "b"] or [8, 9]
      vals = answer
        .flat()
        .filter(notNil)
        .map((v) => String(v))
      isMultiple = vals.length > 1
    } else if (typeof answer === "object") {
      // Object formats from backend
      if (answer.kind === "single" && answer.option != null) {
        // Single correct: {"kind": "single", "option": "c"}
        vals = [String(answer.option)]
        isMultiple = false
      } else if ((answer.kind === "multi" || answer.kind === "multiple") && Array.isArray(answer.options)) {
        // Multiple correct: {"kind": "multi", "options": ["a", "b", "c"]}
        vals = answer.options.filter(notNil).map((v) => String(v))
        isMultiple = vals.length > 1
      } else if (answer.kind === "numeric" && answer.value != null) {
        // Numeric answer: {"kind": "numeric", "value": 8} or {"kind": "numeric", "value": [8, 9]}
        if (Array.isArray(answer.value)) {
          vals = answer.value.filter(notNil).map((v) => String(v))
          isMultiple = vals.length > 1
        } else {
          vals = [String(answer.value)]
          isMultiple = false
        }
      } else if (Array.isArray(answer.options)) {
        // Legacy format with options array
        vals = answer.options.filter(notNil).map((v) => String(v))
        isMultiple = vals.length > 1
      } else if (answer.option != null) {
        // Legacy single option
        vals = [String(answer.option)]
        isMultiple = false
      } else if (answer.value != null) {
        // Legacy value field
        if (Array.isArray(answer.value)) {
          vals = answer.value.filter(notNil).map(String)
          isMultiple = vals.length > 1
        } else {
          vals = [String(answer.value)]
          isMultiple = false
        }
      } else if (answer.selected != null) {
        // Legacy selected field
        vals = [String(answer.selected)]
        isMultiple = false
      } else {
        // Fallback: stringify the object
        vals = [JSON.stringify(answer)]
        isMultiple = false
      }
    } else {
      // Primitive value: string, number, boolean
      vals = [String(answer)]
      isMultiple = false
    }

    // Remove empty values
    vals = vals.filter((v) => v.trim() !== "")

    if (vals.length === 0) {
      return (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="text-sm font-medium text-amber-800">⚠️ No answer provided</div>
        </div>
      )
    }

    return (
      <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg space-y-1">
        <div className="text-sm font-medium text-green-800">{isMultiple ? "Correct Answers:" : "Correct Answer:"}</div>
        <div className="flex flex-wrap gap-2">
          {vals.map((v, i) => (
            <span
              key={i}
              className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 border border-green-200"
            >
              {v}
            </span>
          ))}
        </div>
      </div>
    )
  }
  // Turn ANY correct_answer shape into display text for the small "Answer: ..." chip
  function formatAnswerForInline(ans) {
    if (ans == null) return "-"

    // Handle different backend answer formats
    if (typeof ans === "object" && !Array.isArray(ans)) {
      // Object formats from backend
      if (ans.kind === "single" && ans.option != null) {
        return String(ans.option)
      }
      if ((ans.kind === "multi" || ans.kind === "multiple") && Array.isArray(ans.options)) {
        return ans.options.join(", ")
      }
      if (ans.kind === "numeric" && ans.value != null) {
        return Array.isArray(ans.value) ? ans.value.join(", ") : String(ans.value)
      }
      // Legacy object formats
      if (ans.selected != null) return String(ans.selected)
      if (ans.option != null) return String(ans.option)
      if (ans.options != null) {
        return Array.isArray(ans.options) ? ans.options.join(", ") : String(ans.options)
      }
      if (ans.value != null) {
        return Array.isArray(ans.value) ? ans.value.join(", ") : String(ans.value)
      }

      // Matrix-style maps like {A:"p"} or {A:["p","q"]} → "A-p, A-q"
      const entries = Object.entries(ans)
      if (entries.length) {
        return entries.map(([k, v]) => (Array.isArray(v) ? `${k}-{${v.join(", ")}}` : `${k}-${v}`)).join(", ")
      }
    }

    // Arrays → "a, c" / "3, 5"
    if (Array.isArray(ans)) {
      return ans
        .flat()
        .filter((v) => v != null)
        .map((v) => (typeof v === "object" ? formatAnswerForInline(v) : String(v)))
        .join(", ")
    }

    // Primitives
    if (typeof ans === "string" || typeof ans === "number" || typeof ans === "boolean") {
      return String(ans)
    }

    // Last resort
    return JSON.stringify(ans)
  }

  // replace your handler with this version
  const handleSaveEditedQuestion = async (updatedQuestion) => {
    console.log("[v0] Saving question with data:", updatedQuestion)

    // 1) collect S3 URIs from this question (markdown + fields)
    const newUris = Array.from(new Set(extractImagesFromQuestion(updatedQuestion) || []))

    // 2) presign them first so the list view renders images immediately
    try {
      const map = await presignBatch(newUris) // uses generatePresignedUrl under the hood
      if (map && Object.keys(map).length) {
        setPresignedMap((prev) => ({ ...prev, ...map }))
      }
      // keep the global URI cache in sync
      setImageUris((prev) => Array.from(new Set([...(prev || []), ...newUris])))
    } catch (e) {
      console.warn("[presign][save] skipped", e)
    }

    // 3) update the question in selectedBook (and persist its images: S3 URIs only)
    setSelectedBook((prevBook) => {
      if (!prevBook) return null

      const updatedQuestions = [...prevBook.questions]

      // find existing question by question_number
      const existingQuestionIndex = updatedQuestions.findIndex(
        (q) => q.question_number === updatedQuestion.question_number,
      )

      if (existingQuestionIndex !== -1) {
        // Existing question - update it in place
        console.log("[v0] Updating existing question at index:", existingQuestionIndex)
        console.log("[v0] Previous question data:", updatedQuestions[existingQuestionIndex])

        updatedQuestions[existingQuestionIndex] = {
          ...updatedQuestion,
          isParagraphGroup: undefined, // never persist this flag
          images: newUris, // S3 URIs array
        }

        console.log("[v0] Updated question data:", updatedQuestions[existingQuestionIndex])
      } else {
        // New question - add it with the next available question number
        console.log("[v0] Adding new question")
        const newQuestionNumber = Math.max(...updatedQuestions.map((q) => q.question_number || 0), 0) + 1

        const newQuestion = {
          ...updatedQuestion,
          question_number: newQuestionNumber,
          isParagraphGroup: undefined,
          images: newUris, // S3 URIs array
        }

        updatedQuestions.push(newQuestion)
      }

      const updatedBook = { ...prevBook, questions: updatedQuestions }
      console.log("[v0] Updated book with questions:", updatedBook)
      return updatedBook
    })

    console.log("[v0] Question saved locally:", updatedQuestion)

    // 4) close the modal
    setShowEditModal(false)
    setEditingQuestion(null)
  }

  const handleSaveAllQuestions = async () => {
    if (!selectedBook || !selectedBook._id || !selectedBook.questions) {
      alert("No book or questions to save.")
      return
    }

    try {
      setSavingAllQuestions(true)
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classroomId")
      // Normalize each question: collect clean S3 URIs so backend receives URIs (not presigned)
      const questionsNormalized = (selectedBook.questions || []).map((q) => {
        const images = Array.from(
          new Set([...(Array.isArray(q.images) ? q.images.map(cleanS3) : []), ...extractS3FromObject(q)]),
        ).map(cleanS3)
        return { ...q, images }
      })

      const payload = {
        request: "update-editted-questions",
        body: {
          recordId: selectedBook._id,
          questions: questionsNormalized,
          teacherId: teacherId || "",
          classroomId: classroomId || "",
        },
      }

      const response = await postAws(API_URL, "POST", payload)

      if (response && response.statusCode === 200) {
        alert("All questions saved successfully!")
        console.log("All questions saved response:", response)
      } else {
        alert("Failed to save all questions.")
      }
    } catch (error) {
      console.error("Error saving all questions:", error)
      alert("Error saving all questions: " + error.message)
    } finally {
      setSavingAllQuestions(false)
    }
  }

  const handleDeleteQuestion = async (questionIndex, questionNumber) => {
    setQuestionToDelete({ index: questionIndex, number: questionNumber })
    setShowDeleteConfirm(true)
  }
  const escapeHtml = (s = "") =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return

    try {
      setDeletingQuestionId(questionToDelete.number)

      // Remove question from local state immediately for better UX
      setSelectedBook((prevBook) => {
        if (!prevBook) return null

        const updatedQuestions = prevBook.questions.filter((_, index) => index !== questionToDelete.index)

        // Renumber remaining questions
        const renumberedQuestions = updatedQuestions.map((q, index) => ({
          ...q,
          question_number: index + 1,
        }))

        return { ...prevBook, questions: renumberedQuestions }
      })

      console.log(`Question ${questionToDelete.number} deleted locally`)
    } catch (error) {
      console.error("Error deleting question:", error)
      alert("Error deleting question: " + error.message)
    } finally {
      setDeletingQuestionId(null)
      setShowDeleteConfirm(false)
      setQuestionToDelete(null)
    }
  }

  const cancelDeleteQuestion = () => {
    setShowDeleteConfirm(false)
    setQuestionToDelete(null)
  }

  const handleAddNewQuestion = () => {
    if (!selectedBook) return

    const questionType = selectedBook.questionType || "mcq"
    const newQuestionNumber = (selectedBook.questions?.length || 0) + 1

    // Create a new question template based on the book's question type
    const newQuestion = {
      question_number: newQuestionNumber,
      question_text: "",
      question_type: questionType,
      options: questionType === "mcq" ? { a: "", b: "", c: "", d: "" } : {},
      correctAnswer: "",
      source_references: [],
      passage: questionType === "comprehension" || questionType === "paragraph" ? "" : undefined,
      assertion: questionType === "assertionreasoning" ? "" : undefined,
      reason: questionType === "assertionreasoning" ? "" : undefined,
      column_1: questionType === "matrixmatch" ? {} : undefined,
      column_2: questionType === "matrixmatch" ? {} : undefined,
      answer: questionType === "integer" || questionType === "fillinblanks" ? "" : undefined,
      questions: questionType === "comprehension" || questionType === "paragraph" ? [] : undefined,
    }

    setEditingQuestion(newQuestion)
    setShowEditModal(true)
  }

  const getQuestionTypeDisplay = (questionType) => {
    const typeMap = {
      mcq: "Multiple Choice Questions (MCQ)",
      multiplecorrect: "Multiple Correct Questions",
      truefalse: "True/False Questions",
      fillinblanks: "Fill-in-the-Blanks",
      shortanswer: "Short Answer Questions",
      integer: "Integer/Numerical Questions",
      comprehension: "Comprehension Questions",
      paragraph: "Paragraph-Based Questions",
      casestudy: "Case Study Questions",
      assertionreasoning: "Assertion-Reasoning Questions",
      matrixmatch: "Matrix Matching",
      matchfollowing: "Match-the-Following",
      reasoning: "Reasoning/Critical Thinking",
    }
    return typeMap[questionType] || questionType
  }

  const renderQuestionContent = (question, index) => {
    const questionType = question.question_type || question.original_question_type || "mcq"

    switch (questionType) {
      case "mcq":
      case "multiplecorrect":
      case "truefalse":
      case "reasoning":
        return renderMCQQuestion(question, index)

      case "matrixmatch":
      case "matchfollowing":
        return renderMatrixMatchQuestion(question, index)

      case "comprehension":
      case "paragraph":
      case "casestudy":
        return renderComprehensionQuestion(question, index)

      case "assertionreasoning":
        return renderAssertionReasoningQuestion(question, index)

      case "integer":
      case "shortanswer":
      case "fillinblanks":
        return renderShortAnswerQuestion(question, index)

      default:
        return renderMCQQuestion(question, index)
    }
  }

  const renderMCQQuestion = (question, index) => {
    const toOptionsList = (opts) => {
      if (!opts) return []
      if (Array.isArray(opts)) {
        const base = "abcdefghijklmnopqrstuvwxyz".split("")
        return opts.map((html, i) => ({ key: base[i] ?? String(i + 1), html }))
      }
      if (typeof opts === "object") {
        return Object.entries(opts)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, html]) => ({ key, html }))
      }
      return []
    }

    // Enhanced to handle all backend answer formats
    const parseSelectedKeys = (raw) => {
      const set = new Set()
      const addLetters = (s) => {
        if (!s) return
        const letters = String(s).toLowerCase().match(/[a-z]/g)
        if (letters) letters.forEach((ch) => set.add(ch))
      }
      const addNumbers = (s) => {
        if (!s) return
        const numbers = String(s).match(/\d+/g)
        if (numbers) numbers.forEach((num) => set.add(num))
      }

      if (raw == null) return set

      // Handle structured backend formats
      if (typeof raw === "object" && !Array.isArray(raw)) {
        if (raw.kind === "single" && raw.option) {
          addLetters(raw.option)
          addNumbers(raw.option)
        } else if ((raw.kind === "multi" || raw.kind === "multiple") && Array.isArray(raw.options)) {
          raw.options.forEach((opt) => {
            addLetters(opt)
            addNumbers(opt)
          })
        } else if (raw.kind === "numeric" && raw.value != null) {
          if (Array.isArray(raw.value)) {
            raw.value.forEach((val) => {
              addNumbers(val)
              addLetters(val)
            })
          } else {
            addNumbers(raw.value)
            addLetters(raw.value)
          }
        } else if (raw.selected) {
          addLetters(raw.selected)
          addNumbers(raw.selected)
        } else {
          Object.values(raw).forEach((val) => {
            addLetters(val)
            addNumbers(val)
          })
        }
      } else if (Array.isArray(raw)) {
        raw.forEach((item) => {
          addLetters(item)
          addNumbers(item)
        })
      } else if (typeof raw === "string" || typeof raw === "number") {
        addLetters(raw)
        addNumbers(raw)
      }

      return set
    }

    const rawAnswer = question?.correct_answer ?? question?.correctAnswer ?? question?.answer
    const selectedKeys = parseSelectedKeys(rawAnswer)
    const optionItems = toOptionsList(question?.options ?? question?.choices)

    return (
      <>
        {question?.question_text && (
          <div
            className="text-gray-900 font-medium mb-3"
            dangerouslySetInnerHTML={{ __html: toHtmlWithImages(question.question_text) }}
          />
        )}

        {/* Options with highlight */}
        {optionItems.length > 0 && (
          <div className="mb-3 space-y-2">
            {optionItems.map((opt) => {
              const isCorrect = selectedKeys.has(String(opt.key).toLowerCase()) || selectedKeys.has(String(opt.key))
              return (
                <div
                  key={opt.key}
                  className={
                    "flex gap-2 items-start p-2 rounded border " +
                    (isCorrect ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200")
                  }
                >
                  <span className="font-medium uppercase">{opt.key}.</span>
                  <span className="prose prose-sm" dangerouslySetInnerHTML={{ __html: toHtmlWithImages(opt.html) }} />
                </div>
              )
            })}
          </div>
        )}

        {/* Enhanced correct answer summary */}
        {rawAnswer != null && renderCorrectAnswerBlock(rawAnswer)}
      </>
    )
  }

  const renderMatrixMatchQuestion = (question, index) => {
    // --- helpers ---
    const normalizeMatrixColumns = (q) => {
      const leftRaw = q?.column_1 ?? q?.column1 ?? q?.list1 ?? q?.left ?? null
      const rightRaw = q?.column_2 ?? q?.column2 ?? q?.list2 ?? q?.right ?? null

      const toPairs = (col, fallbackLabels) => {
        if (!col) return []
        if (Array.isArray(col)) {
          return col.map((html, i) => ({ key: fallbackLabels[i] ?? String(i + 1), html }))
        }
        if (typeof col === "object") {
          return Object.entries(col).map(([key, html]) => ({ key, html }))
        }
        return []
      }

      const L = toPairs(leftRaw, ["A", "B", "C", "D", "E", "F", "G"])
      const R = toPairs(rightRaw, ["p", "q", "r", "s", "t", "u", "v"])
      return { L, R }
    }

    // Convert explicit array-of-pairs/maps to [["A","p"],...]
    const pairsFromAnswerArray = (ansArr) => {
      if (!Array.isArray(ansArr)) return []
      const out = []
      for (const item of ansArr) {
        if (Array.isArray(item) && item.length === 2) {
          out.push([String(item[0]), String(item[1])])
          continue
        }
        if (item && typeof item === "object") {
          for (const [k, v] of Object.entries(item)) {
            if (Array.isArray(v)) v.forEach((vv) => out.push([String(k), String(vv)]))
            else out.push([String(k), String(v)])
          }
          continue
        }
        if (typeof item === "string") {
          const m = item.match(/^\s*([A-Za-z0-9]+)\s*(?:-|→|:)\s*([A-Za-z0-9]+)\s*$/)
          if (m) out.push([m[1], m[2]])
        }
      }
      return out
    }

    // Parse option text like:
    // "A-s, B-q, C-r, D-p"
    // "A: {q,s,p}, B: {r,s,p,q}, ..."
    // "A-p, q, r, s, B-p, q, r, s, C-q, r, s, D-p"  (grouped continuation)
    const pairsFromOptionText = (str) => {
      if (!str || typeof str !== "string") return []
      let s = str
        .replace(/–|—/g, "-") // normalize dashes
        .replace(/：/g, ":")
        .replace(/\s+/g, " ")
        .trim()
      // unify A: {...} → A-{...}
      s = s.replace(/([A-Za-z])\s*:\s*\{/g, "$1-{").replace(/\}/g, "}")

      const tokens = s
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
      const out = []
      let currentLeft = null

      const addPair = (L, R) => {
        if (!L || !R) return
        out.push([String(L).trim(), String(R).trim()])
      }

      for (const tok of tokens) {
        // A-{q,s,p}
        let m = tok.match(/^([A-Za-z0-9]+)\s*-\s*\{([^}]+)\}\s*$/)
        if (m) {
          currentLeft = m[1]
          m[2]
            .split(/[,\s]+/)
            .filter(Boolean)
            .forEach((r) => addPair(currentLeft, r))
          continue
        }
        // A-p  or  A-1  or  A→p
        m = tok.match(/^([A-Za-z0-9]+)\s*(?:-|→|:)\s*([A-Za-z0-9]+)\s*$/)
        if (m) {
          currentLeft = m[1]
          addPair(currentLeft, m[2])
          continue
        }
        // continuation: q / r / s / 1 / 2
        m = tok.match(/^([A-Za-z0-9]+)\s*$/)
        if (m && currentLeft) {
          addPair(currentLeft, m[1])
          continue
        }
        // otherwise ignore
      }
      return out
    }

    // Normalize options for display (object or array)
    const normalizeOptionsList = (opts) => {
      if (!opts) return []
      if (Array.isArray(opts)) {
        const base = "abcdefghijklmnopqrstuvwxyz".split("")
        return opts.map((html, i) => ({ key: base[i] ?? String(i + 1), html }))
      }
      if (typeof opts === "object") {
        return Object.entries(opts)
          .sort(([ka], [kb]) => ka.localeCompare(kb))
          .map(([key, html]) => ({ key, html }))
      }
      return []
    }

    // Extract selected option letters from ANY shape
    const parseSelectedOptionKeys = (raw) => {
      const set = new Set()
      const addLetters = (s) => {
        if (!s) return
        const letters = String(s).toLowerCase().match(/[a-z]/g)
        if (letters) letters.forEach((ch) => set.add(ch))
      }
      if (raw == null) return set
      if (typeof raw === "string" || typeof raw === "number") addLetters(raw)
      else if (Array.isArray(raw)) raw.forEach(addLetters)
      else if (typeof raw === "object") {
        if (raw.kind === "single" && raw.option) addLetters(raw.option)
        else if (raw.kind === "multi" && Array.isArray(raw.options)) raw.options.forEach(addLetters)
        else if (raw.selected) addLetters(raw.selected)
        else Object.values(raw).forEach(addLetters)
      }
      return set
    }

    // --- columns (always render if present) ---
    const { L, R } = normalizeMatrixColumns(question)

    // --- options (for both UI and deriving pairs when answer is an option letter) ---
    const optionItems = normalizeOptionsList(question?.options ?? question?.choices ?? question?.options_map)

    // --- raw answer & which options are selected ---
    const rawAnswer = question?.correct_answer ?? question?.correctAnswer ?? question?.answer
    const selectedKeys = parseSelectedOptionKeys(rawAnswer)

    // --- build pairs ---
    let pairs = []
    if (Array.isArray(rawAnswer)) {
      // explicit array answers
      const direct = pairsFromAnswerArray(rawAnswer)
      if (direct.length > 0) {
        pairs = direct
      } else {
        const coerced = rawAnswer.length === 1 ? rawAnswer[0] : rawAnswer.join(",")
        pairs = normalizeMatrixPairs({ ...question, correct_answer: coerced })
      }
    } else if (typeof rawAnswer === "object" && rawAnswer?.kind === "single" && rawAnswer?.option) {
      const optKey = String(rawAnswer.option).toLowerCase()
      const hit = optionItems.find((o) => String(o.key).toLowerCase() === optKey)
      if (hit?.html) {
        pairs = pairsFromOptionText(
          // strip HTML so we can parse text; if you have a util, use it
          String(hit.html).replace(/<[^>]+>/g, ""),
        )
      }
      // If parsing fails, fall back to your existing normalizer
      if (pairs.length === 0) {
        pairs = normalizeMatrixPairs(question)
      }
    } else {
      // string/object flows as before (e.g. "A-p, B-q" or {A:"p", B:[\"q\",\"r\"]})
      pairs = normalizeMatrixPairs(question)
    }

    return (
      <>
        <div
          className="text-gray-900 font-medium mb-4 text-lg"
          dangerouslySetInnerHTML={{ __html: toHtmlWithImages(question.question_text) }}
        />

        {(L.length > 0 || R.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {L.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Column I</div>
                <div className="space-y-2">
                  {L.map((item) => (
                    <div key={item.key} className="flex gap-2">
                      <span className="font-medium">{item.key}.</span>
                      <span
                        className="prose prose-sm"
                        dangerouslySetInnerHTML={{ __html: toHtmlWithImages(item.html) }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {R.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Column II</div>
                <div className="space-y-2">
                  {R.map((item) => (
                    <div key={item.key} className="flex gap-2">
                      <span className="font-medium">{item.key}.</span>
                      <span
                        className="prose prose-sm"
                        dangerouslySetInnerHTML={{ __html: toHtmlWithImages(item.html) }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Options with highlight for selected letters (if present) */}
        {optionItems.length > 0 && (
          <div className="mb-4 p-3 border rounded-lg">
            <div className="text-sm font-semibold mb-2">Options</div>
            <div className="space-y-2">
              {optionItems.map((opt) => {
                const isSelected = selectedKeys.has(String(opt.key).toLowerCase())
                return (
                  <div
                    key={opt.key}
                    className={
                      "flex gap-2 items-start p-2 rounded border " +
                      (isSelected ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200")
                    }
                  >
                    <span className="font-medium uppercase">{opt.key}.</span>
                    <span className="prose prose-sm" dangerouslySetInnerHTML={{ __html: toHtmlWithImages(opt.html) }} />
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {pairs.length ? (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
            <div className="text-sm font-medium text-green-800 mb-1">
              {pairs.length > 1 ? "Correct Matches:" : "Correct Match:"}
            </div>
            <div className="flex flex-wrap gap-2">
              {pairs.map(([l, r], i) => (
                <span key={i} className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                  {String(l)} → {String(r)}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-sm font-medium text-amber-800">⚠️ No matches provided</span>
          </div>
        )}
      </>
    )
  }

  // Drop-in: only affects comprehension rendering.
  // - Inherits parent.correct_answer for sub-questions when they don't have one.
  // - Supports parent answers as array | string/number | mapping object | {kind:"single"/"multi"/"numeric", ...}
  // - Keeps your previous matrix column/options injection.

  const renderComprehensionQuestion = (parent, index) => {
    const pickFirstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null)

    // Fetch parent-level answer in any supported field
    const getParentAnswer = () => pickFirstDefined(parent?.correct_answer, parent?.correctAnswer, parent?.answer)

    // Resolve the answer for a sub-question at index `idx`
    const inheritAnswerForSubQ = (subQ, idx) => {
      // If subQ already has an answer, keep it
      const hasOwn =
        subQ?.correct_answer !== undefined || subQ?.correctAnswer !== undefined || subQ?.answer !== undefined
      if (hasOwn) return pickFirstDefined(subQ?.correct_answer, subQ?.correctAnswer, subQ?.answer)

      const pa = getParentAnswer()
      if (pa == null) return null

      // 1) Array cases (["b"], ["b","c"], [["A","p"],...])
      if (Array.isArray(pa)) {
        if (Array.isArray(pa[0])) return pa[idx] ?? null // array-of-arrays
        if (pa.length === parent?.questions?.length) return pa[idx] ?? null // index-mapped
        if (pa.length === 1) return pa[0] // single value for all
        return pa[idx] ?? null // best-effort
      }

      // 2) Object cases
      if (typeof pa === "object") {
        // 2a) Standardized shapes we must pass through as-is
        //     {kind:"single", option:"c"} | {kind:"multi", options:[...]} | {kind:"numeric", value: ...}
        if (pa.kind === "single" || pa.kind === "multi" || pa.kind === "numeric") {
          return pa
        }

        // 2b) Mapping objects per sub-question number/key
        //     { "1":"c", "2":["a","d"] } or { Q1:"c" }
        const keysToTry = [
          String(subQ?.sub_question_number ?? "").trim(),
          String(subQ?.question_number ?? "").trim(),
          String(idx + 1),
          `Q${idx + 1}`,
          `q${idx + 1}`,
        ].filter(Boolean)

        for (const k of keysToTry) {
          if (pa[k] != null) return pa[k]
        }

        // 2c) Fallback: pass object through (matrix maps etc.)
        return pa
      }

      // 3) Primitive parent answer (string/number) -> use as-is
      return pa
    }

    return (
      <div className="space-y-4">
        {parent?.passage && (
          <div
            className="prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: toHtmlWithImages(parent.passage) }}
          />
        )}

        {(parent?.questions ?? []).map((subQ, idx) => {
          // Compute inherited answer if needed
          const inherited = inheritAnswerForSubQ(subQ, idx)
          let subQWithAns =
            inherited == null
              ? subQ
              : subQ?.correct_answer !== undefined || subQ?.correctAnswer !== undefined || subQ?.answer !== undefined
                ? subQ // already had one; don't overwrite
                : { ...subQ, correct_answer: inherited }

          let subtype = inferQuestionSubtype(subQWithAns)

          // If this is a matrix inside comprehension, inject columns/options from parent if missing
          if (subtype === "matrix") {
            const needsL = !subQWithAns?.column_1 && parent?.column_1
            const needsR = !subQWithAns?.column_2 && parent?.column_2
            const needsOpts = !subQWithAns?.options && parent?.options
            if (needsL || needsR || needsOpts) {
              subtype = "matrix" // keep explicit
              subQWithAns = {
                ...subQWithAns,
                ...(needsL ? { column_1: parent.column_1 } : {}),
                ...(needsR ? { column_2: parent.column_2 } : {}),
                ...(needsOpts ? { options: parent.options } : {}),
              }
            }
          }

          return (
            <div key={idx} className="p-4 rounded-lg border bg-white space-y-3">
              <div className="text-slate-900 font-medium">Q{subQ.sub_question_number ?? idx + 1}.</div>

              {subQWithAns?.question_text && (
                <div
                  className="text-slate-800"
                  dangerouslySetInnerHTML={{ __html: toHtmlWithImages(subQWithAns.question_text) }}
                />
              )}

              {/* Route to existing renderers, but always pass subQWithAns */}
              {subtype === "mcq" || subtype === "mcq-multi" ? (
                <div>{renderMCQQuestion(subQWithAns, idx)}</div>
              ) : subtype === "assertion" ? (
                <div>{renderAssertionReasoningQuestion(subQWithAns, idx)}</div>
              ) : subtype === "matrix" ? (
                <div>{renderMatrixMatchQuestion(subQWithAns, idx)}</div>
              ) : subtype === "numeric" ? (
                <div>{renderNumericAnswer(subQWithAns, idx)}</div>
              ) : (
                <div>{renderShortAnswerQuestion(subQWithAns, idx)}</div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderAssertionReasoningQuestion = (question, index) => {
    const optsLower = toLowerKeyed(question.options || {})
    const norm = normalizeOptionAnswers(question)

    const isMultipleCorrect = norm.type === "options" && (norm.isMultiple || false)
    const correctAnswers = norm.type === "options" ? norm.answers : []

    const hasValidCorrectAnswers = correctAnswers.length > 0 && correctAnswers.every((ans) => ans in optsLower)

    return (
      <>
        <div
          className="text-gray-900 font-medium mb-4 text-lg"
          dangerouslySetInnerHTML={{ __html: toHtmlWithImages(question.question_text) }}
        />

        {/* Assertion and Reason */}
        <div className="mb-4 space-y-4">
          {question.assertion && (
            <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
              <h4 className="font-semibold text-red-800 mb-2">Assertion (A):</h4>
              <div
                className="text-gray-700"
                dangerouslySetInnerHTML={{ __html: toHtmlWithImages(question.assertion) }}
              />
            </div>
          )}
          {question.reason && (
            <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <h4 className="font-semibold text-blue-800 mb-2">Reason (R):</h4>
              <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: toHtmlWithImages(question.reason) }} />
            </div>
          )}
        </div>

        {hasValidCorrectAnswers ? (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-green-800">
                {isMultipleCorrect ? "Correct Answers:" : "Correct Answer:"}
              </span>
              <div className="flex flex-wrap gap-2">
                {correctAnswers.map((ans, idx) => (
                  <span key={idx} className="text-sm text-green-700 bg-green-100 px-2 py-1 rounded">
                    Option {String(ans).toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {correctAnswers.map((ans, idx) => {
                const content = optsLower[ans]
                return content ? (
                  <div
                    key={idx}
                    className="text-sm text-green-600"
                    dangerouslySetInnerHTML={{ __html: toHtmlWithImages(`${String(ans).toUpperCase()}: ${content}`) }}
                  />
                ) : null
              })}
            </div>
          </div>
        ) : question.options ? (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-sm font-medium text-amber-800">
              ⚠️ No correct answer selected - Please edit to select correct answer{isMultipleCorrect ? "s" : ""}
            </span>
          </div>
        ) : null}

        {question.options && (
          <div className="mb-4">
            <span className="text-sm font-medium text-gray-700 mb-3 block">Options:</span>
            <div className="space-y-2">
              {Object.entries(question.options).map(([key, value]) => {
                const keyLower = String(key).toLowerCase()
                const isCorrect = correctAnswers.includes(keyLower)
                return (
                  <div
                    key={key}
                    className={`flex items-start space-x-3 p-3 rounded border ${
                      isCorrect ? "bg-green-50 border-green-300" : "bg-white"
                    }`}
                  >
                    <span className={`font-bold uppercase text-lg ${isCorrect ? "text-green-600" : "text-purple-600"}`}>
                      {key}.
                    </span>
                    <div
                      className="text-gray-700 text-sm flex-1"
                      dangerouslySetInnerHTML={{ __html: toHtmlWithImages(value) }}
                    />
                    {isCorrect && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">✓ Correct</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </>
    )
  }

  const renderShortAnswerQuestion = (question, index) => {
    const rawAnswer = question?.correct_answer ?? question?.correctAnswer ?? question?.answer

    // Handle different backend formats for short answer/numeric questions
    let displayValues = []

    if (rawAnswer && typeof rawAnswer === "object" && !Array.isArray(rawAnswer)) {
      // Handle {"kind": "numeric", "value": 8} format
      if (rawAnswer.kind === "numeric" && rawAnswer.value != null) {
        if (Array.isArray(rawAnswer.value)) {
          displayValues = rawAnswer.value.map((v) => String(v))
        } else {
          displayValues = [String(rawAnswer.value)]
        }
      } else if (rawAnswer.value != null) {
        displayValues = Array.isArray(rawAnswer.value)
          ? rawAnswer.value.map((v) => String(v))
          : [String(rawAnswer.value)]
      } else {
        // Fallback to legacy normalization
        const norm = normalizeFreeformAnswers(question)
        if (norm.kind === "list") {
          displayValues = norm.list
        } else if (norm.kind === "single") {
          displayValues = [norm.value]
        }
      }
    } else if (Array.isArray(rawAnswer)) {
      displayValues = rawAnswer.map((v) => String(v))
    } else if (rawAnswer != null) {
      displayValues = [String(rawAnswer)]
    } else {
      // Fallback to legacy normalization
      const norm = normalizeFreeformAnswers(question)
      if (norm.kind === "list") {
        displayValues = norm.list
      } else if (norm.kind === "single") {
        displayValues = [norm.value]
      }
    }

    const hasAnswers = displayValues.length > 0 && displayValues.some((v) => v.trim() !== "")
    const isMultiple = displayValues.length > 1

    return (
      <>
        {question?.question_text && (
          <div
            className="text-gray-900 font-medium mb-4 text-lg"
            dangerouslySetInnerHTML={{ __html: toHtmlWithImages(question.question_text) }}
          />
        )}

        {hasAnswers ? (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <span className="text-sm font-medium text-green-800">
              {isMultiple ? "Correct Answers:" : "Correct Answer:"}
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {displayValues.map((val, i) => (
                <span key={i} className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded border border-green-200">
                  {val}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-sm font-medium text-amber-800">⚠️ No answer provided</span>
          </div>
        )}
      </>
    )
  }

  // Numeric renderer: supports
  // number | string
  // array<number|string>
  // { kind: "numeric", value: number|string|array<number|string> }
  const renderNumericAnswer = (question, index) => {
    const raw = question?.correct_answer ?? question?.correctAnswer ?? question?.answer

    // Normalize to an array of displayable strings
    let values = []

    if (raw && typeof raw === "object" && raw.kind === "numeric") {
      const v = raw.value
      values = Array.isArray(v) ? v : [v]
    } else if (Array.isArray(raw)) {
      values = raw
    } else if (raw !== undefined && raw !== null) {
      values = [raw]
    }

    // Flatten one level & stringify
    values = values
      .flat()
      .filter((v) => v !== undefined && v !== null)
      .map((v) => String(v))

    if (values.length === 0) {
      return (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-sm font-medium text-amber-800">⚠️ No numeric answer provided</span>
        </div>
      )
    }

    const multi = values.length > 1

    return (
      <div className="mb-3">
        <div className="text-sm font-semibold mb-1">{multi ? "Correct Answers:" : "Correct Answer:"}</div>
        <div className="flex flex-wrap gap-2">
          {values.map((val, i) => (
            <span
              key={i}
              className="inline-block px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
            >
              {val}
            </span>
          ))}
        </div>
      </div>
    )
  }

  // Function to get available option keys and missing ones
  const getOptionInfo = (options) => {
    const allOptions = ["a", "b", "c", "d"]
    const existingOptions = options ? Object.keys(options) : []
    const missingOptions = allOptions.filter((opt) => !existingOptions.includes(opt))
    return {
      existing: existingOptions,
      missing: missingOptions,
      canAddMore: existingOptions.length < 4,
    }
  }

  // Function to add a missing option to a question
  const handleAddOption = (questionIndex, optionKey) => {
    setSelectedBook((prevBook) => {
      if (!prevBook) return null

      const updatedQuestions = [...prevBook.questions]
      const question = updatedQuestions[questionIndex]

      if (!question.options) {
        question.options = {}
      }

      question.options[optionKey] = "New option - please edit"

      return { ...prevBook, questions: updatedQuestions }
    })
  }

  // Get the correct answer option content

  if (!showModal) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-screen h-screen overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{selectedBook?.book || "Loading..."}</h2>
            {selectedBook && (
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                <span className="flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  Class {selectedBook.class}
                </span>
                <span className="flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  {selectedBook.subject}
                </span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {formatDate(selectedBook.updatedAt)}
                </span>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedBook.status)}`}
                >
                  {selectedBook.status}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  {getQuestionTypeDisplay(selectedBook.questionType)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {selectedBook && (
              <button
                onClick={handleAddNewQuestion}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Question</span>
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {bookLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading questions...</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">{error}</p>
              <button onClick={() => fetchBookById(bookId)} className="mt-2 text-blue-600 hover:text-blue-800">
                Try again
              </button>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Questions ({selectedBook?.questions?.length || 0}) - Sorted by Question Number
              </h3>

              {selectedBook?.questions && selectedBook.questions.length > 0 ? (
                <div className="space-y-8">
                  {selectedBook.questions.map((question, index) => {
                    return (
                      <div key={index} className="border rounded-lg p-6 bg-gray-50">
                        <div className="flex items-start space-x-4">
                          <span className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {question.question_number || index + 1}
                          </span>
                          <div className="flex-1">
                            <div className="mb-3">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                {getQuestionTypeDisplay(question.question_type || question.original_question_type)}
                              </span>
                            </div>

                            {renderQuestionContent(question, index)}

                            {/* Source References */}
                            {question.source_references && question.source_references.length > 0 && (
                              <div className="mt-4">
                                <span className="font-medium text-gray-700 text-sm">References:</span>
                                <div className="mt-2 space-y-2">
                                  {question.source_references.map((ref, refIndex) => (
                                    <div key={refIndex} className="bg-white p-3 rounded border">
                                      <div
                                        className="text-gray-600 text-sm"
                                        dangerouslySetInnerHTML={{ __html: toHtmlWithImages(ref.text) }}
                                      />
                                      {ref.page && <div className="text-xs text-gray-400 mt-1">Page {ref.page}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Question Metadata */}
                            <div className="mt-4 flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => handleEditQuestionClick(question)}
                                  className="inline-flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-600 transition-colors"
                                >
                                  <Edit className="h-4 w-4" />
                                  <span>Edit Question</span>
                                </button>

                                <button
                                  onClick={() => handleDeleteQuestion(index, question.question_number || index + 1)}
                                  disabled={deletingQuestionId === (question.question_number || index + 1)}
                                  className="inline-flex items-center space-x-2 bg-red-500 text-white px-4 py-2 rounded-md text-sm hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {deletingQuestionId === (question.question_number || index + 1) ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>No questions available for this book</p>
                  {selectedBook && (
                    <button
                      onClick={handleAddNewQuestion}
                      className="mt-4 inline-flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                      <span>Add First Question</span>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="p-6 border-t flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4" />
            <span>Close</span>
          </button>
          <button
            onClick={handleSaveAllQuestions}
            disabled={savingAllQuestions || !selectedBook?.questions?.length}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingAllQuestions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span>{savingAllQuestions ? "Saving..." : "Save All Questions"}</span>
          </button>
        </div>
      </div>

      {/* Question Editor Modal */}
      <QuestionEditorModal
        question={editingQuestion}
        showModal={showEditModal}
        onClose={handleCloseEditModal}
        onSave={handleSaveEditedQuestion}
        imageUris={modalImageUris}
      />

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete Question {questionToDelete?.number}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteQuestion}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteQuestion}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddQuestionQuestionbankEditExtractedQuestions
